import React, { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { compact } from "lodash";
import PaddedCell from "@cayuse/shared-react.components.padded-cell";
import { isNonEmptyArray, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";

import {
	AWARDS_BASE_ROUTE,
	COLUMN_HEADER_KEYS,
	DEFAULT_SUBAWARD_COLUMN_HEADERS,
	HIDE_FROM_RELEASE,
	MODIFICATION_DRAFT
} from "globals/constants";
import { findSubawards } from "store/actions/subawardList";
import {
	columnsSelectors,
	subawardListSelectors,
	tenantSelectors,
} from "store/selectors";
import { setSubawardsActiveColumns } from "store/actions/columns";
import { resetModifications } from "store/actions/modifications";
import { resetForm } from "store/actions/form";
import { useColumnHeaders, useFormAccesses, useComponentDidMount } from "hooks";
import { getKeysFromColumnHeaders, sortCollectionByNestedKey, spUrl } from "utils";

import SetView from "components/SetView";
import StartNewSubawardFromAward from "./StartNewSubawardFromAward";
import FeedTable from "../../../../components/FeedTable";
import SPLink from "../../../../components/SPLink";

import styles from "./styles.css";

const pageLimit = 100; // Max-number of subawards due to numbering, no paging scoped.
const pageOffset = 0;

const SUBAWARD_LIST_DEFAULT_COLUMNS = [
	DEFAULT_SUBAWARD_COLUMN_HEADERS.SUBAWARD_NUMBER,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.STATUS,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.SUBAWARDEE,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.SUBAWARD_BEGIN_DATE,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.SUBAWARD_END_DATE,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.TOTAL_EXPECTED,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.PRIMARY_ADMIN_CONTACT,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.PRIMARY_CONTACT_EMAIL,
	DEFAULT_SUBAWARD_COLUMN_HEADERS.RESOLUTION
];

export const Subaward = () => {
	const { awardId } = useParams();

	const [ isSetViewOpen, setIsSetViewOpen ] = useState(false);
	const [ sort, setSort ] = useState({
		column: DEFAULT_SUBAWARD_COLUMN_HEADERS.SUBAWARD_NUMBER.keys[0],
		direction: "descending"
	});
	const [ subawards, setSubawards ] = useState([]);

	const { items } = useSelector(subawardListSelectors.content);
	const isSubawardLoading = useSelector(subawardListSelectors.isFetching);
	const { activeColumns: storedActiveColumns } = useSelector(columnsSelectors.subawards);

	const componentDidMount = useComponentDidMount();
	const formAccesses = useFormAccesses("award", awardId);
	const { columnHeaders: availableColumns, defaultColumnHeaders } = useColumnHeaders(COLUMN_HEADER_KEYS.SUBAWARD, {
		defaultColumnHeaders: SUBAWARD_LIST_DEFAULT_COLUMNS
	});

	const hasItems = isNonEmptyArray(items);
	const onViewChanged = selectedColumns => setSubawardsActiveColumns(selectedColumns);

	const activeColumns = useMemo(() => {
		const columns = isNonEmptyArray(storedActiveColumns)
			? compact(storedActiveColumns)
			: defaultColumnHeaders;

		return columns.map(column => ({
			...column,
			sortKey: column.keys[0] === sort.column ? sort.column : null
		}));
	}, [ defaultColumnHeaders, storedActiveColumns ]);

	const formColumnHeaders = useSelector(tenantSelectors.subawardFormColumnHeaders);
	const updatedActiveColumns = activeColumns.map(activeColumn => formColumnHeaders.find(formColumnHeader => activeColumn.id === formColumnHeader.id));

	const requiredFieldKeys = useMemo(() => getKeysFromColumnHeaders(activeColumns), [ activeColumns ]);

	const sortColumnByKey = (key, sortOrder) => {
		const originalDataKey = `_item.${key}`;
		return sortOrder === "descending"
			? sortCollectionByNestedKey(subawards, originalDataKey).reverse()
			: sortCollectionByNestedKey(subawards, originalDataKey);
	};

	const handleSort = (column, direction) => {
		setSort({ column , direction });
		setSubawards(sortColumnByKey(column, direction));
	};

	useEffect(()=> setSubawardsActiveColumns(updatedActiveColumns),[ awardId ])

	useEffect(() => {
		if(componentDidMount) {
			findSubawards({
				awardId: awardId,
				pageLimit,
				pageOffset,
				fieldKeys: requiredFieldKeys
			});
		}
	}, [ awardId, componentDidMount, requiredFieldKeys ]);

	const onSubawardClick = () => {
		resetModifications();
		resetForm();
	};

	useEffect(() => {
		if (hasItems && items.some(item => item.awardId === awardId)) {
			const updatedItems = hasItems
				? items.map(item => {
					const linkedSubawardNumber = isNonEmptyString(item.subawardNumber)
						? <SPLink to={`${AWARDS_BASE_ROUTE}/${item.awardId}/sub-awards/${item.subawardId}`} onClick={onSubawardClick}>{item.subawardNumber}</SPLink>
						: "";

					(item.subRows ?? []).forEach(subRow => {
						if (subRow.modificationStatus === MODIFICATION_DRAFT) {
							subRow.subawardNumber = <span className={styles.draftSubawardNumber}>{subRow.subawardNumber} (Draft)</span>;
						}
					});

					return {
						_item: item,
						...item,
						subawardNumber: linkedSubawardNumber,
						_isSelected: false
					};
				})
				: [];

			setSubawards(updatedItems);
		}
	}, [ awardId, hasItems, items ]);

	return (
		<>
			<div>
				<PaddedCell className={styles.subawardListOptions}>
					{HIDE_FROM_RELEASE && // Hiding unfinished SetView - work to be scoped later.
						<SetView
							isOpen={isSetViewOpen}
							options={availableColumns}
							selections={activeColumns}
							onOpen={() => setIsSetViewOpen(true)}
							onClose={() => setIsSetViewOpen(false)}
							onUpdate={onViewChanged}
							requireKeys={false}
						/>
					}
					{formAccesses.subawardCreate &&
						<div className={styles.createSubawardButton}>
							<StartNewSubawardFromAward parentId={awardId} />
						</div>
					}
				</PaddedCell>
				<FeedTable
					tableid={"subawardsTable"}
					captionText="Subawards"
					isLoading={isSubawardLoading}
					headers={activeColumns}
					rows={subawards}
					pageLimit={false}
					isExpandable={true}
					overflow="visible"
					noResultsLabel={"No subawards have been added yet."}
					sort={sort}
					onSortChange={handleSort}
				/>
			</div>
		</>
	);
};

export default Subaward;
